export interface ReportListItem {
  report_id: string;
  case_id: string;
  kit_code: string;
  product_type: string;
  cycle_phase: string;
  status: string;
  assigned_at: string | null;
  created_at: string;
  test_date: string | null;
  has_lab_pdf: boolean;
  has_published_pdf: boolean;
}

export interface ReportListResponse {
  items: ReportListItem[];
  total: number;
  page: number;
  per_page: number;
}

export interface HormoneCard {
  hormone_code: string;
  value: number;
  unit: string;
  reference_range: string;
  status: string;
  general_explanation?: string;
  patient_interpretation?: string;
  doctor_interpretation?: string;
  age_context?: string;
}

export interface MatchedScenario {
  scenario_id: string;
  pattern_name: string;
  clinical_interpretation: string;
  clinical_relevance_doctor?: string;
}

export interface LifestyleData {
  exercise_frequency: string | null;
  activity_types: string | null;
  activity_other: string | null;
  sleep_hours: string | null;
  stress_level: string | null;
  diet_type: string | null;
  diet_other: string | null;
}

export interface HealthConditionsData {
  hormonal_conditions: string | null;
  hormonal_other: string | null;
  chronic_conditions: string | null;
  chronic_other: string | null;
}

export interface TreatmentHistoryData {
  hormonal_therapy: boolean | null;
  therapy_type: string | null;
  therapy_other: string | null;
  taking_supplements: boolean | null;
  supplements_detail: string | null;
  treatment_plan: boolean | null;
  treatment_detail: string | null;
}

export interface OnboardingData {
  cycle_length_days: number | null;
  cycle_description: string | null;
  cramps: string | null;
  bleeding_pattern: string | null;
  bleeding_other: string | null;
  goals: string[];
  symptoms: string[];
  has_other_symptoms: boolean | null;
  other_symptoms: string | null;
  lifestyle: LifestyleData;
  health_conditions: HealthConditionsData;
  treatment_history: TreatmentHistoryData;
}

export const EXERCISE_FREQUENCY_LABELS: Record<string, string> = {
  "0-1": "0-1 days / week",
  "2-3": "2-3 days / week",
  "4-5": "4-5 days / week",
  "6-7": "6-7 days / week",
  prefer_not_to_say: "Prefer not to say",
};

export const ACTIVITY_TYPE_LABELS: Record<string, string> = {
  cardio: "Cardio",
  strength_training: "Strength training",
  yoga_pilates: "Yoga / Pilates",
  team_sports: "Team sports",
  low_impact_walking: "Low-impact walking",
  none: "None",
  other: "Other",
};

export const SLEEP_HOURS_LABELS: Record<string, string> = {
  "<5": "Less than 5 hours",
  "5-7": "5-7 hours",
  "7-9": "7-9 hours",
  ">9": "More than 9 hours",
};

export const STRESS_LEVEL_LABELS: Record<string, string> = {
  low: "Low",
  moderate: "Moderate",
  high: "High",
  very_high: "Very high",
};

export const DIET_TYPE_LABELS: Record<string, string> = {
  omnivore: "Omnivore",
  pescatarian: "Pescatarian",
  vegetarian: "Vegetarian",
  vegan: "Vegan",
  high_protein: "High protein",
  balanced: "Balanced",
  other: "Other",
};

export const HORMONAL_CONDITION_LABELS: Record<string, string> = {
  pcos: "PCOS",
  thyroid_disorder: "Thyroid disorder",
  endometriosis: "Endometriosis",
  perimenopause_menopause: "Perimenopause / Menopause",
  other: "Other",
};

export const CHRONIC_CONDITION_LABELS: Record<string, string> = {
  diabetes: "Diabetes",
  hypertension: "Hypertension",
  autoimmune: "Autoimmune condition",
  cardiovascular: "Cardiovascular disease",
  other: "Other",
};

export const THERAPY_TYPE_LABELS: Record<string, string> = {
  pill: "Birth control pill",
  hormonal_iud: "Hormonal IUD",
  copper_iud: "Copper IUD",
  implant: "Implant",
  hrt: "Hormone replacement therapy",
  fertility_treatment: "Fertility treatment",
  other: "Other",
};

export interface ReportDetail {
  report_id: string;
  case_id: string;
  kit_code: string;
  product_type: string;
  cycle_phase: string;
  status: string;
  assigned_at: string | null;
  drafted_at: string | null;
  published_at: string | null;
  test_date: string | null;
  patient_age: number | null;
  has_lab_pdf: boolean;
  has_published_pdf: boolean;
  // True when the lab has sent a corrected/amended result for this kit
  // (hiya-backend-9vyn / sotg). Drives the "data may be outdated" badge.
  correction_received?: boolean;
  doctor_name: string | null;
  signature_url: string | null;
  final_assessment: string | null;
  plan_next_steps: string | null;
  cycle_summary: string | null;
  non_cycle_summary: string | null;
  attestation_checked: boolean;
  hormone_cards: HormoneCard[];
  matched_scenarios: MatchedScenario[];
  cycle_context: { phase: string; patient_context: string; doctor_context: string } | null;
  report_sections: Record<string, unknown>[];
  onboarding: OnboardingData | null;
}

export interface SaveReportDraftPayload {
  final_assessment: string | null;
  plan_next_steps: string | null;
  cycle_summary: string | null;
  non_cycle_summary: string | null;
}

export interface ReportPublishResponse {
  status: string;
  published_at: string;
  report_id: string;
}

export const PRODUCT_LABELS: Record<string, string> = {
  hiya_sync: "HIYA Sync",
  hiya_foundation: "HIYA Foundation",
  hiya_journey: "HIYA Journey",
  hiya_secure: "HIYA Secure",
};

export const PHASE_LABELS: Record<string, string> = {
  day_3: "Day 3 (Early Follicular)",
  day_21: "Day 21 (Luteal Phase)",
};

export const HORMONE_LABELS: Record<string, string> = {
  E2: "Estradiol",
  PRL: "Prolactin",
  PROG: "Progesterone",
  FSH: "FSH",
  LH: "LH",
  AMH: "AMH",
  TSH: "TSH",
};

export const BLEEDING_PATTERN_LABELS: Record<string, string> = {
  heavy_flow: "Heavy flow during period",
  spotting_before: "Spotting before period",
  spotting_between: "Spotting between periods",
  spotting_after: "Spotting after period",
};

export const NON_CYCLE_HORMONE_CODES: ReadonlySet<string> = new Set(["AMH", "TSH", "PRL"]);
